<h2 mat-dialog-title>{{ data.title }}</h2>

<mat-dialog-content>
  @if (data.period_label) {
    <p class="period">{{ data.period_label }}</p>
  }

  <div class="invoice">
    <div class="invoice-head">
      <span>Concepto</span>
      <span>Importe</span>
    </div>

    <div class="invoice-row">
      <span class="concept">Base convenio</span>
      <span class="amount">{{ signedAmount(data.breakdown.brute_base_salary) }}</span>
    </div>

    @if (data.breakdown.seniority_bonus) {
      <div class="invoice-row">
        <span class="concept">Seniority</span>
        <span class="amount positive">{{ signedAmount(data.breakdown.seniority_bonus) }}</span>
      </div>
    }

    @if (hasPlusesDetail) {
      <details class="invoice-details">
        <summary>
          <span class="concept"><span class="emoji" aria-hidden="true">🎖️</span> Pluses Bisual</span>
          <span class="amount" [class.positive]="data.breakdown.pluses_total > 0">
            {{ signedAmount(data.breakdown.pluses_total) }}
          </span>
        </summary>
        <div class="invoice-detail-body">
          @if (data.justification?.bisual_plus_original_amount != null) {
            <p class="detail-hint">
              El plus Bisual es salario Bisual - base convenio. Se reduce según la media de los KPIs personales del mes anterior.
            </p>
            <div class="detail-row">
              <span>Sin KPIs cobrarías</span>
              <span>{{ data.justification!.bisual_plus_original_amount | currency }}</span>
            </div>
            <div class="detail-row">
              <span>
                Cobras
                @if (data.justification!.kpi_average_factor != null) {
                  <small>({{ data.justification!.kpi_average_factor | percent: '1.0-0' }} media KPIs)</small>
                }
              </span>
              <span>{{ data.justification!.bisual_plus_amount_after_kpi | currency }}</span>
            </div>
            @if (kpiReductionAmount != null && kpiReductionAmount > 0) {
              <div class="detail-row">
                <span>Dejas de cobrar</span>
                <span class="negative">−{{ kpiReductionAmount | currency }}</span>
              </div>
            }
          }

          @if (data.justification?.pluses?.length) {
            <p class="detail-title">Reparto</p>
            @for (plus of data.justification!.pluses; track plus.plus_name) {
              <div class="detail-row">
                <span>{{ plus.plus_name }}</span>
                <span>{{ plus.amount | currency }}</span>
              </div>
            }
          }

          @if (data.justification?.kpis?.length) {
            <p class="detail-title">KPIs (mes anterior)</p>
            @for (row of data.justification!.kpis; track row.kpi_name) {
              <div class="kpi-block">
                <div class="detail-row kpi">
                  <span>
                    {{ row.kpi_name }}
                    ·
                    <span
                      [class.positive]="row.evaluation === 'good'"
                      [class.is-medium-eval]="row.evaluation === 'medium'"
                      [class.negative]="row.evaluation === 'poor'"
                    >
                      {{ evaluationLabel(row.evaluation) }}
                    </span>
                    @if (row.poor_streak > 0) {
                      ·
                      <span class="negative">racha {{ row.poor_streak }}</span>
                    }
                  </span>
                  <span [class.positive]="row.factor >= 1" [class.negative]="row.factor < 1">
                    {{ row.factor | percent: '1.0-0' }}
                  </span>
                </div>
                @if (row.history?.length) {
                  <div class="kpi-history" [attr.aria-label]="'Historial ' + row.kpi_name">
                    @for (item of row.history; track item.month) {
                      <span
                        class="kpi-history-chip"
                        [class.is-good]="item.evaluation === 'good'"
                        [class.is-medium]="item.evaluation === 'medium'"
                        [class.is-poor]="item.evaluation === 'poor'"
                        [class.is-empty]="!item.evaluation || item.evaluation === 'not_calculable'"
                        [matTooltip]="item.label + ': ' + evaluationLabel(item.evaluation)"
                      >
                        <span class="kpi-history-month">{{ item.label }}</span>
                        <span class="kpi-history-eval">{{ evaluationShortLabel(item.evaluation) }}</span>
                      </span>
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      </details>
    } @else {
      <div class="invoice-row">
        <span class="concept"><span class="emoji" aria-hidden="true">🎖️</span> Pluses Bisual</span>
        <span class="amount" [class.positive]="data.breakdown.pluses_total > 0">
          {{ signedAmount(data.breakdown.pluses_total) }}
        </span>
      </div>
    }

    @if (data.breakdown.flexible_remuneration > 0) {
      <div class="invoice-row">
        <span class="concept">
          <span class="emoji" aria-hidden="true">🍽️</span>
          Ret. flexible
          <small>{{ data.breakdown.extract_flexible_remuneration_from_salary ? 'excluida' : 'incluida' }}</small>
        </span>
        <span
          class="amount"
          [class.positive]="!data.breakdown.extract_flexible_remuneration_from_salary && data.breakdown.flexible_remuneration > 0"
        >
          {{ signedAmount(data.breakdown.flexible_remuneration) }}
        </span>
      </div>
    }

    @if (hasFuel) {
      <details class="invoice-details">
        <summary>
          <span class="concept"><span class="emoji" aria-hidden="true">⛽</span> Gasolina</span>
          <span class="amount positive">{{ signedAmount(data.breakdown.total_fuel_cost) }}</span>
        </summary>
        <div class="invoice-detail-body">
          <div class="detail-row">
            <span>Distancia</span>
            <span>{{ data.breakdown.distance_in_km | number: '1.0-2' }} km</span>
          </div>
          <div class="detail-row">
            <span>Precio / km</span>
            <span>{{ data.breakdown.fuel_cost_per_km | currency }}</span>
          </div>
          <div class="detail-row">
            <span>Compensación</span>
            <span class="positive">{{ signedAmount(data.breakdown.total_fuel_cost) }}</span>
          </div>
          @if (data.can_view_mileage_detail && data.payroll_id) {
            <button mat-stroked-button type="button" class="detail-action" (click)="openMileageDetail()">Ver viajes</button>
          }
        </div>
      </details>
    }

    @if (hasExtra) {
      <details class="invoice-details">
        <summary>
          <span class="concept"><span class="emoji" aria-hidden="true">⏱️</span> Horas extra</span>
          <span class="amount positive">
            @if (data.breakdown.additional_hours_amount > 0) {
              {{ signedAmount(data.breakdown.additional_hours_amount) }}
            } @else if (data.breakdown.additional_salary > 0) {
              {{ signedAmount(data.breakdown.additional_salary) }}
            }
          </span>
        </summary>
        <div class="invoice-detail-body">
          <div class="detail-row">
            <span>Horas</span>
            <span>{{ data.breakdown.additional_hours }} h</span>
          </div>
          @if (data.breakdown.additional_hours_amount > 0) {
            <div class="detail-row">
              <span>Importe</span>
              <span class="positive">{{ signedAmount(data.breakdown.additional_hours_amount) }}</span>
            </div>
            <p class="detail-hint">
              Cálculo: {{ data.breakdown.additional_hours }} h × (base convenio {{ data.breakdown.brute_base_salary | currency }} × 10 ÷
              1800) = {{ data.breakdown.additional_hours_amount | currency }}.
            </p>
          } @else if (data.breakdown.additional_salary > 0) {
            <div class="detail-row">
              <span>Importe</span>
              <span class="positive">{{ signedAmount(data.breakdown.additional_salary) }}</span>
            </div>
          }
          <p class="detail-hint">Solo cuentan horas con solicitud aprobada (tope semanal).</p>
        </div>
      </details>
    }

    @if (hasAbsenceNotes) {
      <details class="invoice-details">
        <summary>
          <span class="concept">Notas</span>
          <span class="amount muted">···</span>
        </summary>
        <div class="invoice-detail-body">
          @if (data.breakdown.comment_for_employee?.trim()) {
            <p class="detail-note">{{ data.breakdown.comment_for_employee }}</p>
          }
          @if (data.breakdown.comments?.trim()) {
            <p class="detail-note muted">{{ data.breakdown.comments }}</p>
          }
        </div>
      </details>
    }

    <div class="invoice-total">
      <span>SM Calculado</span>
      <strong>{{ data.breakdown.sm_calculated | currency }}</strong>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="openPlusesPolicyArticle()" [disabled]="openingWikiArticle">Cómo funcionan los pluses</button>
  <button mat-flat-button color="primary" type="button" (click)="close()">Cerrar</button>
</mat-dialog-actions>
